import { Button, IconButton, Stack, TextField, Tooltip } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { GridColDef } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import type { Item, Project } from '../../core/types/data-models';
import { GenerateItemsDialog } from './GenerateItemsDialog';

/**
 * Items list. Demonstrates the M:1 relation: each row's `projectId` is
 * resolved to the project's name via a shared `useQuery` cache key
 * (`['projects', 'select']`) that the EntitySelect on the form also uses,
 * so we only fetch the project catalog once per session.
 *
 * The "Generate items" button opens a dialog that drives the LLM-backed
 * resource action `POST /api/v1/items/generate-items/:projectId` — see
 * the action under `backend/src/modules/items/items/actions/` for the
 * server side of the same flow.
 */
export function ItemsPage() {
  const navigate = useNavigate();
  const [generateOpen, setGenerateOpen] = useState(false);

  const projectsLookup = useQuery({
    queryKey: ['projects', 'select'],
    queryFn: () => resources.projects.list({ pageSize: 200, sort: 'name' }),
  });

  const projectName = useMemo(() => {
    const m = new Map<number, string>();
    for (const p of projectsLookup.data?.data ?? []) m.set(p.id, p.name);
    return m;
  }, [projectsLookup.data]);

  // Default the dialog to the first project so the user usually only
  // has to click "Generate". They can still pick a different project
  // inside the dialog.
  const defaultProjectId = projectsLookup.data?.data?.[0]?.id ?? null;

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 220,
        renderCell: (p) => (
          <RouterLink to={`/items/${p.row.id}`}>{p.value as string}</RouterLink>
        ),
      },
      {
        field: 'projectId',
        headerName: 'Project',
        width: 200,
        renderCell: (p) => projectName.get(p.value as number) ?? `#${p.value}`,
      },
      { field: 'description', headerName: 'Description', flex: 2, minWidth: 220 },
    ],
    [projectName],
  );

  return (
    <>
      <CrudPage<Item>
        title="Items"
        entityName="Item"
        description="Tenant-scoped items, each belonging to a project (M:1). Open an item to manage its detail row and tags."
        cacheKey={['items']}
        service={resources.items}
        columns={columns}
        defaultValues={{ name: '', description: '', projectId: undefined }}
        headerActions={
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => setGenerateOpen(true)}
          >
            Generate items
          </Button>
        }
        rowActions={(row) => (
          <Tooltip title="Open item">
            <IconButton
              size="small"
              color="primary"
              onClick={() => navigate(`/items/${row.id}`)}
              aria-label="open item"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        renderForm={({ values, setValues }) => (
          <Stack spacing={2}>
            <TextField
              label="Name"
              required
              value={values.name ?? ''}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
            />
            <EntitySelect<Project>
              label="Project"
              required
              helperText="The owning project (many-to-1)."
              cacheKey={['projects', 'select']}
              service={resources.projects}
              value={values.projectId ?? null}
              onChange={(id) => setValues({ ...values, projectId: id ?? undefined })}
            />
            <TextField
              label="Description"
              multiline
              minRows={2}
              value={values.description ?? ''}
              onChange={(e) => setValues({ ...values, description: e.target.value })}
            />
          </Stack>
        )}
      />
      <GenerateItemsDialog
        open={generateOpen}
        defaultProjectId={defaultProjectId}
        onClose={() => setGenerateOpen(false)}
      />
    </>
  );
}
